import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Plus, Minus, ShoppingCart, X, LogOut, Edit, Package, Send, CheckCircle, FileText } from 'lucide-react';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import { Input } from '../components/common/Input';
import Footer from '../components/layout/Footer';
import { theme, media } from '../styles/theme';
import ordersService from '../services/ordersService';

const Container = styled.div`
  min-height: 100vh;
  background-color: ${theme.colors.background};
  padding: ${theme.spacing.lg};
  padding-bottom: 80px;
  position: relative;

  ${media.down.sm} {
    padding: ${theme.spacing.sm};
    padding-bottom: 100px;
  }

  ${media.down.xs} {
    padding: ${theme.spacing.xs};
    padding-bottom: 100px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.xl};
  padding: ${theme.spacing.lg};
  background-color: ${theme.colors.white};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.md};
  flex-wrap: wrap;
  gap: ${theme.spacing.md};

  h1 {
    font-size: ${theme.typography.fontSize['2xl']};
    font-weight: ${theme.typography.fontWeight.bold};
    color: ${theme.colors.text};
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing.md};
    h1 {
      font-size: ${theme.typography.fontSize.xl};
    }
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.lg};
  flex-wrap: wrap;

  ${media.down.sm} {
    flex-direction: column;
    align-items: flex-start;
    gap: ${theme.spacing.md};
    width: 100%;
  }
`;

const TableInfo = styled.span`
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.text};
  font-weight: ${theme.typography.fontWeight.medium};

  ${media.down.sm} {
    font-size: ${theme.typography.fontSize.sm};
  }
`;

const LogoutButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};

  ${media.down.sm} {
    width: 100%;
    justify-content: center;

    .logout-text {
      display: inline;
    }
  }
`;

const TableGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.xl};

  ${media.down.md} {
    grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
    gap: ${theme.spacing.sm};
  }

  ${media.down.sm} {
    grid-template-columns: repeat(auto-fill, minmax(90px, 1fr));
    gap: ${theme.spacing.sm};
  }

  ${media.down.xs} {
    grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
    gap: ${theme.spacing.xs};
  }
`;

const TableCard = styled(Card)`
  padding: ${theme.spacing.lg};
  text-align: center;
  cursor: pointer;
  border: 3px solid ${({ selected }) => selected ? theme.colors.primary : 'transparent'};
  background-color: ${({ status, selected }) => {
    if (selected) return `${theme.colors.primary}10`;
    if (status === 'occupied') return `${theme.colors.status.preparing}10`;
    return theme.colors.white;
  }};
  transition: all 0.2s ease;
  min-height: 80px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.md};
  }

  &:active {
    transform: scale(0.98);
  }

  h3 {
    font-size: ${theme.typography.fontSize.lg};
    font-weight: ${theme.typography.fontWeight.semiBold};
    margin-bottom: ${theme.spacing.xs};
    color: ${theme.colors.text};
  }

  p {
    font-size: ${theme.typography.fontSize.sm};
    color: ${theme.colors.textSecondary};
  }

  ${media.down.sm} {
    padding: ${theme.spacing.md};
    min-height: 70px;
    
    h3 {
      font-size: ${theme.typography.fontSize.base};
    }
    
    p {
      font-size: ${theme.typography.fontSize.xs};
    }
  }

  ${media.down.xs} {
    padding: ${theme.spacing.sm};
    min-height: 60px;
    
    h3 {
      font-size: ${theme.typography.fontSize.sm};
    }
    
    p {
      font-size: 10px;
    }
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: ${theme.spacing.lg};

  ${media.down.lg} {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.md};
  }
`;

const MenuSection = styled.div`
  background-color: ${theme.colors.white};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.lg};
  box-shadow: ${theme.shadows.md};
  max-height: calc(100vh - 200px);
  overflow-y: auto;

  ${media.down.lg} {
    max-height: none;
    margin-bottom: ${theme.spacing.lg};
  }

  ${media.down.sm} {
    padding: ${theme.spacing.md};
  }
`;

const CategoryTabs = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.lg};
  flex-wrap: wrap;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding-bottom: ${theme.spacing.xs};

  &::-webkit-scrollbar {
    display: none;
  }

  ${media.down.sm} {
    gap: ${theme.spacing.xs};
    margin-bottom: ${theme.spacing.md};
  }
`;

const CategoryTab = styled.button`
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.full};
  border: 2px solid ${({ active }) => active ? theme.colors.primary : theme.colors.border};
  background-color: ${({ active }) => active ? theme.colors.primary : theme.colors.white};
  color: ${({ active }) => active ? theme.colors.white : theme.colors.text};
  font-weight: ${({ active }) => active ? theme.typography.fontWeight.semiBold : theme.typography.fontWeight.regular};
  font-family: ${theme.typography.fontFamily};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: ${theme.typography.fontSize.sm};
  white-space: nowrap;
  min-height: 44px;
  -webkit-tap-highlight-color: transparent;

  &:active {
    transform: scale(0.95);
  }

  ${media.down.sm} {
    padding: ${theme.spacing.xs} ${theme.spacing.md};
    font-size: ${theme.typography.fontSize.xs};
    min-height: 40px;
  }
`;

const MenuGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: ${theme.spacing.lg};

  ${media.down.md} {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: ${theme.spacing.md};
  }

  ${media.down.sm} {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.sm};
  }
`;

const MenuItemCard = styled(Card)`
  padding: ${theme.spacing.lg};
  position: relative;
  opacity: ${({ disabled }) => disabled ? 0.5 : 1};
  cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  min-height: 120px;

  ${({ disabled }) => !disabled && `
    &:hover {
      transform: translateY(-2px);
      box-shadow: ${theme.shadows.md};
    }

    &:active {
      transform: scale(0.98);
    }
  `}

  ${media.down.sm} {
    padding: ${theme.spacing.md};
    min-height: 100px;
  }

  ${media.down.xs} {
    padding: ${theme.spacing.sm};
    min-height: 90px;
  }
`;

const AvailabilityBadge = styled.span`
  position: absolute;
  top: ${theme.spacing.sm};
  right: ${theme.spacing.sm};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.semiBold};
  background-color: ${({ available }) => available ? theme.colors.status.ready : theme.colors.status.outOfStock};
  color: ${theme.colors.white};
`;

const MenuItemName = styled.h3`
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.semiBold};
  margin-bottom: ${theme.spacing.xs};
  color: ${theme.colors.text};
`;

const MenuItemPrice = styled.p`
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.primary};
  margin-bottom: ${theme.spacing.md};
`;

const QuantitySelector = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  justify-content: space-between;
`;

const QuantityControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  border: 2px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.xs};
`;

const QuantityButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: ${theme.borderRadius.md};
  background-color: ${theme.colors.secondary};
  color: ${theme.colors.text};
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: ${theme.typography.fontFamily};
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;

  &:hover {
    background-color: ${theme.colors.primary};
    color: ${theme.colors.white};
  }

  &:active {
    transform: scale(0.9);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  ${media.down.sm} {
    width: 36px;
    height: 36px;
    min-width: 36px;
    min-height: 36px;
  }
`;

const QuantityValue = styled.span`
  font-weight: ${theme.typography.fontWeight.semiBold};
  min-width: 30px;
  text-align: center;
`;


const CartSection = styled.div`
  background-color: ${theme.colors.white};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.lg};
  box-shadow: ${theme.shadows.md};
  position: sticky;
  top: ${theme.spacing.lg};
  max-height: calc(100vh - 100px);
  display: flex;
  flex-direction: column;
  pointer-events: auto;
  z-index: 1;

  ${media.down.lg} {
    position: relative;
    top: 0;
    max-height: none;
    margin-bottom: ${theme.spacing.lg};
  }

  ${media.down.sm} {
    padding: ${theme.spacing.md};
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    max-height: 70vh;
    border-radius: ${theme.borderRadius.xl} ${theme.borderRadius.xl} 0 0;
    box-shadow: ${theme.shadows['2xl']};
    z-index: ${theme.zIndex.modal};
    transform: translateY(100%);
    transition: transform 0.3s ease;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    pointer-events: auto;
    
    &.open {
      transform: translateY(0);
      pointer-events: auto;
    }
    
    &:not(.open) {
      pointer-events: none;
    }
  }

  ${media.down.xs} {
    max-height: 75vh;
    padding: ${theme.spacing.sm};
  }
`;

const CartHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.lg};
  padding-bottom: ${theme.spacing.md};
  border-bottom: 2px solid ${theme.colors.border};

  h2 {
    font-size: ${theme.typography.fontSize.xl};
    font-weight: ${theme.typography.fontWeight.semiBold};
    color: ${theme.colors.text};
  }
`;

const CartItems = styled.div`
  flex: 1;
  overflow-y: auto;
  margin-bottom: ${theme.spacing.lg};
`;

const CartItem = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.sm};
  background-color: ${theme.colors.secondary};
  border-radius: ${theme.borderRadius.md};
  gap: ${theme.spacing.sm};

  ${media.down.sm} {
    padding: ${theme.spacing.sm};
  }
`;

const CartItemRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${theme.spacing.sm};

  ${media.down.sm} {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const CartItemInfo = styled.div`
  flex: 1;

  h4 {
    font-size: ${theme.typography.fontSize.base};
    font-weight: ${theme.typography.fontWeight.semiBold};
    margin-bottom: ${theme.spacing.xs};
    color: ${theme.colors.text};
  }

  p {
    font-size: ${theme.typography.fontSize.sm};
    color: ${theme.colors.textSecondary};
  }
`;

const NotesInput = styled(Input)`
  margin-top: ${theme.spacing.xs};
  font-size: ${theme.typography.fontSize.sm};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border: 1px solid ${theme.colors.border};
  
  &::placeholder {
    font-size: ${theme.typography.fontSize.xs};
    color: ${theme.colors.textLight};
  }
`;

const OrderNotesSection = styled.div`
  margin-top: ${theme.spacing.lg};
  padding-top: ${theme.spacing.md};
  border-top: 1px solid ${theme.colors.border};
`;

const OrderNotesLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semiBold};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.sm};
  
  svg {
    width: 16px;
    height: 16px;
    color: ${theme.colors.primary};
  }
`;

const MobileCartToggle = styled.button`
  display: none;
  margin-left: auto;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: ${theme.colors.text};
  padding: ${theme.spacing.xs};
  width: 44px;
  height: 44px;
  align-items: center;
  justify-content: center;
  border-radius: ${theme.borderRadius.md};
  transition: all 0.2s ease;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  min-width: 44px;
  min-height: 44px;

  &:hover {
    background-color: ${theme.colors.secondary};
  }

  &:active {
    transform: scale(0.9);
    background-color: ${theme.colors.secondary};
  }

  ${media.down.sm} {
    display: flex;
  }
`;

const FloatingCartButton = styled.button`
  display: none;
  position: fixed;
  bottom: ${theme.spacing.lg};
  right: ${theme.spacing.lg};
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primaryLight} 100%);
  color: ${theme.colors.white};
  border: none;
  cursor: pointer;
  box-shadow: ${theme.shadows.xl};
  align-items: center;
  justify-content: center;
  z-index: ${theme.zIndex.fixed + 1};
  transition: all 0.3s ease;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;

  &:hover {
    transform: scale(1.1);
    box-shadow: ${theme.shadows['2xl']};
  }

  &:active {
    transform: scale(0.95);
  }

  ${media.down.sm} {
    display: ${({ show }) => show ? 'flex' : 'none'};
    width: 56px;
    height: 56px;
    bottom: ${theme.spacing.md};
    right: ${theme.spacing.md};
  }

  ${media.down.xs} {
    width: 52px;
    height: 52px;
    bottom: ${theme.spacing.sm};
    right: ${theme.spacing.sm};
  }

  .cart-badge {
    position: absolute;
    top: -5px;
    right: -5px;
    background: ${theme.colors.status.outOfStock};
    color: ${theme.colors.white};
    border-radius: 50%;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: ${theme.typography.fontSize.xs};
    font-weight: ${theme.typography.fontWeight.bold};
    min-width: 24px;
  }
`;

const CartItemActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};

  ${media.down.sm} {
    width: 100%;
    justify-content: space-between;
  }
`;

const RemoveButton = styled.button`
  width: 28px;
  height: 28px;
  border-radius: ${theme.borderRadius.md};
  background-color: ${theme.colors.status.outOfStock};
  color: ${theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;

  &:hover {
    opacity: 0.8;
  }

  &:active {
    transform: scale(0.9);
  }

  ${media.down.sm} {
    width: 36px;
    height: 36px;
    min-width: 36px;
    min-height: 36px;
  }
`;

const CartTotal = styled.div`
  padding-top: ${theme.spacing.md};
  border-top: 2px solid ${theme.colors.border};
  margin-bottom: ${theme.spacing.lg};

  div {
    display: flex;
    justify-content: space-between;
    margin-bottom: ${theme.spacing.sm};
    font-size: ${theme.typography.fontSize.base};
    color: ${theme.colors.text};

    &:last-child {
      font-size: ${theme.typography.fontSize.xl};
      font-weight: ${theme.typography.fontWeight.bold};
      color: ${theme.colors.primary};
      margin-top: ${theme.spacing.md};
      padding-top: ${theme.spacing.md};
      border-top: 2px solid ${theme.colors.border};
    }
  }
`;

const ExistingOrderSection = styled(Card)`
  margin-bottom: ${theme.spacing.lg};
  padding: ${theme.spacing.lg};
  background: ${theme.colors.white};
  border: 2px solid ${theme.colors.primary};
  
  ${media.down.sm} {
    padding: ${theme.spacing.md};
    margin-bottom: ${theme.spacing.md};
  }
`;

const ExistingOrderHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.md};
  padding-bottom: ${theme.spacing.md};
  border-bottom: 2px solid ${theme.colors.border};
  
  h3 {
    font-size: ${theme.typography.fontSize.lg};
    font-weight: ${theme.typography.fontWeight.bold};
    color: ${theme.colors.text};
    display: flex;
    align-items: center;
    gap: ${theme.spacing.sm};
  }
`;

const ExistingOrderItems = styled.div`
  margin-bottom: ${theme.spacing.md};
`;

const ExistingOrderItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${theme.spacing.sm} 0;
  border-bottom: 1px solid ${theme.colors.border};
  
  &:last-child {
    border-bottom: none;
  }
  
  .item-info {
    flex: 1;
    
    .item-name {
      font-weight: ${theme.typography.fontWeight.semiBold};
      color: ${theme.colors.text};
      margin-bottom: ${theme.spacing.xs};
    }
    
    .item-details {
      font-size: ${theme.typography.fontSize.sm};
      color: ${theme.colors.textSecondary};
    }
  }
  
  .item-actions {
    display: flex;
    align-items: center;
    gap: ${theme.spacing.sm};
  }

  ${media.down.sm} {
    flex-direction: column;
    align-items: flex-start;
    gap: ${theme.spacing.sm};
    
    .item-actions {
      width: 100%;
      justify-content: space-between;
    }
  }
`;

const OrderStatusBadge = styled.span`
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.semiBold};
  background: ${({ status }) => {
    if (status === 'completed' || status === 'ready_for_billing') return `${theme.colors.status.ready}20`;
    if (status === 'preparing') return `${theme.colors.status.preparing}20`;
    return `${theme.colors.status.pending}20`;
  }};
  color: ${({ status }) => {
    if (status === 'completed' || status === 'ready_for_billing') return theme.colors.status.ready;
    if (status === 'preparing') return theme.colors.status.preparing;
    return theme.colors.status.pending;
  }};
`;

const EditOrderButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;

  ${media.down.sm} {
    min-height: 44px;
    padding: ${theme.spacing.sm} ${theme.spacing.md};
    font-size: ${theme.typography.fontSize.sm};
  }
`;

const categories = ['All', 'Starters', 'Main Course', 'Desserts', 'Beverages'];
const tables = Array.from({ length: 20 }, (_, i) => ({
  id: i + 1,
  number: i + 1,
  status: i % 3 === 0 ? 'occupied' : 'available',
}));

// Mock existing orders data
const mockExistingOrders = {
  5: {
    id: 'ORD-001',
    tableNumber: 5,
    status: 'preparing',
    items: [
      { id: 1, name: 'Paneer Tikka', quantity: 2, price: 250 },
      { id: 2, name: 'Butter Naan', quantity: 4, price: 50 },
    ],
    total: 700,
    timestamp: '2:30 PM',
  },
  12: {
    id: 'ORD-002',
    tableNumber: 12,
    status: 'pending',
    items: [
      { id: 2, name: 'Chicken Biryani', quantity: 2, price: 350 },
    ],
    total: 700,
    timestamp: '2:45 PM',
  },
};

const menuItems = [
  { id: 1, name: 'Paneer Tikka', price: 250, category: 'Starters', available: true },
  { id: 2, name: 'Chicken Biryani', price: 350, category: 'Main Course', available: true },
  { id: 3, name: 'Butter Naan', price: 50, category: 'Main Course', available: true },
  { id: 4, name: 'Gulab Jamun', price: 80, category: 'Desserts', available: false },
  { id: 5, name: 'Mango Lassi', price: 120, category: 'Beverages', available: true },
  { id: 6, name: 'Samosa', price: 60, category: 'Starters', available: true },
  { id: 7, name: 'Dal Makhani', price: 200, category: 'Main Course', available: true },
  { id: 8, name: 'Ice Cream', price: 100, category: 'Desserts', available: true },
];

function WaiterOrdering() {
  const navigate = useNavigate();
  const [selectedTable, setSelectedTable] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [existingOrders, setExistingOrders] = useState({});
  const [isEditingOrder, setIsEditingOrder] = useState(false);
  const [loading, setLoading] = useState(false);
  const [orderNotes, setOrderNotes] = useState('');

  // Fetch existing orders from backend
  useEffect(() => {
    fetchExistingOrders();
    // Poll for updates every 5 seconds
    const interval = setInterval(fetchExistingOrders, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchExistingOrders = async () => {
    try {
      const orders = await ordersService.getAll();
      // Group orders by table number
      // Exclude completed, cancelled, and ready_for_billing orders (sent to counter)
      // Tables with ready_for_billing orders should be available for new orders
      const ordersByTable = {};
      orders.forEach(order => {
        if (order.status !== 'completed' && 
            order.status !== 'cancelled' && 
            order.status !== 'ready_for_billing') {
          // Map items to include notes from specialInstructions
          const mappedItems = (order.items || []).map(item => ({
            ...item,
            notes: item.specialInstructions || item.notes || '',
            name: item.itemName || item.name,
          }));
          
          ordersByTable[order.tableNumber] = {
            id: order.id,
            orderNumber: order.orderNumber,
            tableNumber: order.tableNumber,
            status: order.status,
            items: mappedItems,
            total: parseFloat(order.total) || 0,
            subtotal: parseFloat(order.subtotal) || 0,
            gst: parseFloat(order.gst) || 0,
            discount: parseFloat(order.discount) || 0,
            waiterName: order.waiterName || '',
            notes: order.notes || '',
            createdAt: order.createdAt
          };
        }
      });
      setExistingOrders(ordersByTable);
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  const handleLogout = () => {
    navigate('/login');
  };

  const handleAddToCart = (item) => {
    if (!item.available) return;
    const existingItem = cart.find((cartItem) => cartItem.id === item.id);
    if (existingItem) {
      setCart(
        cart.map((cartItem) =>
          cartItem.id === item.id
            ? { ...cartItem, quantity: cartItem.quantity + 1 }
            : cartItem
        )
      );
    } else {
      setCart([...cart, { ...item, quantity: 1, notes: '' }]);
    }
  };

  const handleUpdateQuantity = (itemId, change) => {
    setCart(
      cart.map((item) => {
        if (item.id === itemId) {
          const newQuantity = item.quantity + change;
          if (newQuantity <= 0) return null;
          return { ...item, quantity: newQuantity };
        }
        return item;
      }).filter(Boolean)
    );
  };

  const handleRemoveFromCart = (itemId) => {
    setCart(cart.filter((item) => item.id !== itemId));
  };

  const handleUpdateItemNotes = (itemId, notes) => {
    setCart(
      cart.map((item) =>
        item.id === itemId ? { ...item, notes: notes } : item
      )
    );
  };

  const handlePlaceOrder = async () => {
    if (!selectedTable || cart.length === 0) return;
    
    setLoading(true);
    try {
      const orderItems = cart.map(item => ({
        name: item.name,
        quantity: item.quantity,
        price: item.price,
        notes: item.notes || '',
        specialInstructions: item.notes || '', // Backend expects specialInstructions
      }));

      if (isEditingOrder && existingOrders[selectedTable.number]) {
        // Update existing order
        const existingOrder = existingOrders[selectedTable.number];
        const existingItems = (existingOrder.items || []).map(item => ({
          name: item.itemName || item.name,
          quantity: item.quantity || item.qty,
          price: parseFloat(item.price || 0),
        }));
        const newItems = [...existingItems, ...orderItems];
        const newSubtotal = newItems.reduce((sum, item) => sum + (parseFloat(item.price) * item.quantity), 0);
        const newGst = newSubtotal * 0.18;
        const newTotal = newSubtotal + newGst;

        await ordersService.update(existingOrder.id, {
          items: newItems,
          subtotal: newSubtotal,
          gst: newGst,
          total: newTotal,
          notes: orderNotes.trim() || null,
        });

        alert(`Items added to existing order for Table ${selectedTable.number}!`);
        await fetchExistingOrders();
      } else {
        // Create new order
        const newOrder = await ordersService.create({
          tableNumber: selectedTable.number.toString(),
          waiterName: 'Waiter', // You can get this from login/session
          items: orderItems,
          subtotal: subtotal,
          gst: gst,
          discount: 0,
          total: total,
          status: 'pending',
        });

        alert(`Order placed for Table ${selectedTable.number}! Order sent to kitchen.`);
        await fetchExistingOrders();
      }
      
      setCart([]);
      setOrderNotes('');
      setIsEditingOrder(false);
    } catch (error) {
      console.error('Error placing order:', error);
      alert(`Failed to place order: ${error.message || 'Please try again'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEditOrder = () => {
    if (!selectedTable || !existingOrders[selectedTable.number]) return;
    setIsEditingOrder(true);
    const existingOrder = existingOrders[selectedTable.number];
    // Load existing order items into cart
    const cartItems = (existingOrder.items || []).map(item => {
      const menuItem = menuItems.find(mi => mi.id === item.id || mi.name === item.name || mi.name === item.itemName);
      return {
        ...(menuItem || { id: Date.now(), name: item.itemName || item.name, price: item.price, category: 'All', available: true }),
        quantity: item.quantity || item.qty || 1,
        notes: item.notes || item.specialInstructions || '',
      };
    });
    setCart(cartItems);
    setOrderNotes(existingOrder.notes || '');
    setCartOpen(true);
  };

  const handleRemoveFromExistingOrder = async (tableNumber, itemId) => {
    const order = existingOrders[tableNumber];
    if (!order) return;
    
    try {
      const updatedItems = (order.items || []).filter(item => {
        return item.id !== itemId && item.itemName !== itemId && item.name !== itemId;
      });
      
      if (updatedItems.length === 0) {
        // Delete order if no items left
        await ordersService.remove(order.id);
        await fetchExistingOrders();
        setSelectedTable(null);
        return;
      }
      
      const newSubtotal = updatedItems.reduce((sum, item) => {
        const price = parseFloat(item.price || 0);
        const qty = item.quantity || item.qty || 1;
        return sum + (price * qty);
      }, 0);
      const newGst = newSubtotal * 0.18;
      const newTotal = newSubtotal + newGst;
      
      await ordersService.update(order.id, {
        items: updatedItems.map(item => ({
          name: item.itemName || item.name,
          quantity: item.quantity || item.qty,
          price: parseFloat(item.price),
        })),
        subtotal: newSubtotal,
        gst: newGst,
        total: newTotal,
      });
      
      await fetchExistingOrders();
    } catch (error) {
      console.error('Error removing item from order:', error);
      alert('Failed to remove item. Please try again.');
    }
  };

  const handleSendToCounter = async (tableNumber) => {
    const order = existingOrders[tableNumber];
    if (!order) {
      alert('No order found for this table');
      return;
    }

    if (order.status === 'ready_for_billing') {
      alert('Order already sent to counter for billing!');
      return;
    }

    if (!window.confirm(`Send order ${order.orderNumber || order.id} to counter for billing?`)) {
      return;
    }

    setLoading(true);
    try {
      await ordersService.update(order.id, {
        status: 'ready_for_billing',
      });

      alert(`Order sent to counter successfully! Table ${tableNumber} is ready for billing.`);
      await fetchExistingOrders();
    } catch (error) {
      console.error('Error sending order to counter:', error);
      alert(`Failed to send order to counter: ${error.message || 'Please try again'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateExistingOrderQuantity = async (tableNumber, itemId, change) => {
    const order = existingOrders[tableNumber];
    if (!order) return;
    
    try {
      const updatedItems = (order.items || []).map(item => {
        const itemMatch = item.id === itemId || item.itemName === itemId || item.name === itemId;
        if (itemMatch) {
          const newQuantity = (item.quantity || item.qty || 1) + change;
          if (newQuantity <= 0) return null;
          return { 
            ...item, 
            quantity: newQuantity,
            qty: newQuantity
          };
        }
        return item;
      }).filter(Boolean);
      
      if (updatedItems.length === 0) {
        // Delete order if no items left
        await ordersService.remove(order.id);
        await fetchExistingOrders();
        setSelectedTable(null);
        return;
      }
      
      const newSubtotal = updatedItems.reduce((sum, item) => {
        const price = parseFloat(item.price || 0);
        const qty = item.quantity || item.qty || 1;
        return sum + (price * qty);
      }, 0);
      const newGst = newSubtotal * 0.18;
      const newTotal = newSubtotal + newGst;
      
      await ordersService.update(order.id, {
        items: updatedItems.map(item => ({
          name: item.itemName || item.name,
          quantity: item.quantity || item.qty,
          price: parseFloat(item.price),
        })),
        subtotal: newSubtotal,
        gst: newGst,
        total: newTotal,
      });
      
      await fetchExistingOrders();
    } catch (error) {
      console.error('Error updating order quantity:', error);
      alert('Failed to update order. Please try again.');
    }
  };

  const filteredMenu = selectedCategory === 'All'
    ? menuItems
    : menuItems.filter((item) => item.category === selectedCategory);

  const subtotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const gst = subtotal * 0.18;
  const total = subtotal + gst;

  // Auto-open cart on mobile when items are added
  useEffect(() => {
    if (cart.length > 0 && window.innerWidth <= parseInt(theme.breakpoints.sm)) {
      setCartOpen(true);
    }
  }, [cart.length]);

  // Handle window resize for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > parseInt(theme.breakpoints.sm)) {
        setCartOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Reset editing mode when table changes
  useEffect(() => {
    setIsEditingOrder(false);
    setCart([]);
  }, [selectedTable?.number]);

  const currentOrder = selectedTable ? existingOrders[selectedTable.number] : null;

  return (
    <Container>
      <Header>
        <h1>Waiter Ordering</h1>
        <HeaderActions>
          <TableInfo>Table: {selectedTable ? `Table ${selectedTable.number}` : 'Select a table'}</TableInfo>
          <LogoutButton variant="secondary" size="sm" onClick={handleLogout}>
            <LogOut size={18} />
            <span className="logout-text">Logout</span>
          </LogoutButton>
        </HeaderActions>
      </Header>

      <TableGrid>
        {tables.map((table) => (
          <TableCard
            key={table.id}
            selected={selectedTable?.id === table.id}
            status={existingOrders[table.number] ? 'occupied' : table.status}
            onClick={() => setSelectedTable(table)}
          >
            <h3>Table {table.number}</h3>
            <p>
              {existingOrders[table.number] 
                ? `Order: ${existingOrders[table.number].status.charAt(0).toUpperCase() + existingOrders[table.number].status.slice(1)}` 
                : table.status === 'occupied' 
                  ? 'Occupied' 
                  : 'Available'}
            </p>
          </TableCard>
        ))}
      </TableGrid>

      {currentOrder && (
        <ExistingOrderSection>
          <ExistingOrderHeader>
            <h3>
              <Package size={20} />
              Existing Order - {currentOrder.orderNumber || currentOrder.id}
            </h3>
            <div style={{ display: 'flex', gap: theme.spacing.sm, alignItems: 'center', flexWrap: 'wrap' }}>
              <OrderStatusBadge status={currentOrder.status}>
                {currentOrder.status === 'ready_for_billing' ? 'Ready for Billing' : 
                 currentOrder.status.charAt(0).toUpperCase() + currentOrder.status.slice(1)}
              </OrderStatusBadge>
              {currentOrder.status !== 'ready_for_billing' && currentOrder.status !== 'cancelled' && (
                <EditOrderButton
                  variant="primary"
                  size="sm"
                  onClick={handleEditOrder}
                >
                  <Edit size={16} />
                  Edit Order
                </EditOrderButton>
              )}
              {currentOrder.status !== 'ready_for_billing' && currentOrder.status !== 'cancelled' && (
                <EditOrderButton
                  variant="accent"
                  size="sm"
                  onClick={() => handleSendToCounter(selectedTable.number)}
                  disabled={loading}
                >
                  <Send size={16} />
                  Send to Counter
                </EditOrderButton>
              )}
              {currentOrder.status === 'ready_for_billing' && (
                <EditOrderButton
                  variant="secondary"
                  size="sm"
                  disabled
                >
                  <CheckCircle size={16} />
                  Sent to Counter
                </EditOrderButton>
              )}
            </div>
          </ExistingOrderHeader>
          
          <ExistingOrderItems>
            {(currentOrder.items || []).map((item, index) => {
              const itemName = item.itemName || item.name || 'Item';
              const quantity = item.quantity || item.qty || 1;
              const price = parseFloat(item.price || 0);
              const itemId = item.id || itemName || index;
              return (
                <ExistingOrderItem key={itemId || index}>
                  <div className="item-info">
                    <div className="item-name">{itemName}</div>
                    <div className="item-details">
                      ₹{price} × {quantity} = ₹{(price * quantity).toFixed(2)}
                    </div>
                    {(item.notes || item.specialInstructions) && (
                      <div style={{
                        marginTop: theme.spacing.xs,
                        fontSize: theme.typography.fontSize.xs,
                        color: theme.colors.primary,
                        fontStyle: 'italic',
                        paddingLeft: theme.spacing.xs,
                        borderLeft: `2px solid ${theme.colors.primary}40`,
                      }}>
                        <FileText size={12} style={{ marginRight: theme.spacing.xs, verticalAlign: 'middle' }} />
                        {item.notes || item.specialInstructions}
                      </div>
                    )}
                  </div>
                  <div className="item-actions">
                    <QuantityControls>
                      <QuantityButton
                        onClick={() => handleUpdateExistingOrderQuantity(selectedTable.number, itemId, -1)}
                      >
                        <Minus size={14} />
                      </QuantityButton>
                      <QuantityValue>{quantity}</QuantityValue>
                      <QuantityButton
                        onClick={() => handleUpdateExistingOrderQuantity(selectedTable.number, itemId, 1)}
                      >
                        <Plus size={14} />
                      </QuantityButton>
                    </QuantityControls>
                    <RemoveButton
                      onClick={() => handleRemoveFromExistingOrder(selectedTable.number, itemId)}
                    >
                      <X size={14} />
                    </RemoveButton>
                  </div>
                </ExistingOrderItem>
              );
            })}
          </ExistingOrderItems>
          
          {currentOrder.notes && (
            <div style={{
              padding: theme.spacing.md,
              marginTop: theme.spacing.md,
              backgroundColor: `${theme.colors.primary}10`,
              borderRadius: theme.borderRadius.md,
              borderLeft: `3px solid ${theme.colors.primary}`,
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: theme.spacing.xs,
                marginBottom: theme.spacing.xs,
                fontSize: theme.typography.fontSize.sm,
                fontWeight: theme.typography.fontWeight.semiBold,
                color: theme.colors.text,
              }}>
                <FileText size={14} />
                Order Notes:
              </div>
              <div style={{
                fontSize: theme.typography.fontSize.sm,
                color: theme.colors.textSecondary,
                lineHeight: 1.5,
              }}>
                {currentOrder.notes}
              </div>
            </div>
          )}
          
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            paddingTop: theme.spacing.md,
            borderTop: `2px solid ${theme.colors.border}`,
            marginTop: theme.spacing.md,
            flexWrap: 'wrap',
            gap: theme.spacing.sm
          }}>
            <div>
              <div style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.textSecondary }}>
                {currentOrder.createdAt ? `Time: ${new Date(currentOrder.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}` : currentOrder.timestamp ? `Time: ${currentOrder.timestamp}` : ''}
              </div>
            </div>
            <div style={{ 
              fontSize: theme.typography.fontSize.lg, 
              fontWeight: theme.typography.fontWeight.bold,
              color: theme.colors.primary
            }}>
              Total: ₹{currentOrder.total}
            </div>
          </div>
        </ExistingOrderSection>
      )}

      <ContentGrid>
        <MenuSection>
          <CategoryTabs>
            {categories.map((category) => (
              <CategoryTab
                key={category}
                active={selectedCategory === category}
                onClick={() => setSelectedCategory(category)}
              >
                {category}
              </CategoryTab>
            ))}
          </CategoryTabs>

          <MenuGrid>
            {filteredMenu.map((item) => (
              <MenuItemCard
                key={item.id}
                disabled={!item.available}
                onClick={() => handleAddToCart(item)}
              >
                <AvailabilityBadge available={item.available}>
                  {item.available ? 'Available' : 'Out of Stock'}
                </AvailabilityBadge>
                <MenuItemName>{item.name}</MenuItemName>
                <MenuItemPrice>₹{item.price}</MenuItemPrice>
                <QuantitySelector>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddToCart(item);
                    }}
                    disabled={!item.available}
                  >
                    <Plus size={16} />
                  </Button>
                </QuantitySelector>
              </MenuItemCard>
            ))}
          </MenuGrid>
        </MenuSection>

        <CartSection className={cartOpen || cart.length > 0 ? 'open' : ''}>
          <CartHeader>
            <ShoppingCart size={24} />
            <h2>Order Cart {cart.length > 0 && <span>({cart.length})</span>}</h2>
            <MobileCartToggle
              onClick={() => setCartOpen(!cartOpen)}
              className="mobile-only"
            >
              {cartOpen ? '×' : '☰'}
            </MobileCartToggle>
          </CartHeader>

          <CartItems>
            {cart.length === 0 ? (
              <p style={{ textAlign: 'center', color: theme.colors.textSecondary, padding: theme.spacing.xl }}>
                Cart is empty
              </p>
            ) : (
              cart.map((item) => (
                <CartItem key={item.id}>
                  <CartItemRow>
                    <CartItemInfo>
                      <h4>{item.name}</h4>
                      <p>₹{item.price} × {item.quantity} = ₹{item.price * item.quantity}</p>
                    </CartItemInfo>
                    <CartItemActions>
                      <QuantityControls>
                        <QuantityButton
                          onClick={() => handleUpdateQuantity(item.id, -1)}
                        >
                          <Minus size={16} />
                        </QuantityButton>
                        <QuantityValue>{item.quantity}</QuantityValue>
                        <QuantityButton
                          onClick={() => handleUpdateQuantity(item.id, 1)}
                        >
                          <Plus size={16} />
                        </QuantityButton>
                      </QuantityControls>
                      <RemoveButton onClick={() => handleRemoveFromCart(item.id)}>
                        <X size={16} />
                      </RemoveButton>
                    </CartItemActions>
                  </CartItemRow>
                  <NotesInput
                    type="text"
                    placeholder={`Add note for ${item.name} (e.g., "Less spicy", "Extra cheese", "No onions")`}
                    value={item.notes || ''}
                    onChange={(e) => handleUpdateItemNotes(item.id, e.target.value)}
                  />
                </CartItem>
              ))
            )}
          </CartItems>

          {cart.length > 0 && (
            <>
              <CartTotal>
                <div>
                  <span>Subtotal:</span>
                  <span>₹{subtotal.toFixed(2)}</span>
                </div>
                <div>
                  <span>GST (18%):</span>
                  <span>₹{gst.toFixed(2)}</span>
                </div>
                <div>
                  <span>Total:</span>
                  <span>₹{total.toFixed(2)}</span>
                </div>
              </CartTotal>

              <OrderNotesSection>
                <OrderNotesLabel>
                  <FileText size={16} />
                  Order Notes (Optional)
                </OrderNotesLabel>
                <NotesInput
                  type="text"
                  placeholder="Add general order notes (e.g., 'Customer birthday', 'Rush order', 'Special occasion')"
                  value={orderNotes}
                  onChange={(e) => setOrderNotes(e.target.value)}
                  rows={2}
                  as="textarea"
                  style={{
                    minHeight: '60px',
                    resize: 'vertical',
                    fontFamily: theme.typography.fontFamily,
                  }}
                />
              </OrderNotesSection>

              <Button
                variant="primary"
                size="lg"
                fullWidth
                onClick={handlePlaceOrder}
                disabled={!selectedTable || loading}
                style={{ marginTop: theme.spacing.md }}
              >
                {loading ? 'Placing Order...' : isEditingOrder ? 'Update Order' : 'Place Order'}
              </Button>
            </>
          )}
        </CartSection>
      </ContentGrid>

      {cart.length > 0 && !cartOpen && (
        <FloatingCartButton 
          show={cart.length > 0 && !cartOpen}
          onClick={() => setCartOpen(true)}
        >
          <ShoppingCart size={24} />
          {cart.length > 0 && (
            <span className="cart-badge">{cart.length}</span>
          )}
        </FloatingCartButton>
      )}

      <Footer hasSidebar={false} />
    </Container>
  );
}

export default WaiterOrdering;

